import React from 'react';
import { PageHeader, Section } from '../components/PageShell';
import { Button } from '../components/Button';
import {
  UserCheck, LayoutDashboard, MessageSquareWarning, FileCheck2,
  Building2, Boxes, Users, Bot, BarChart3, PieChart, Wrench
} from 'lucide-react';

const solutions = [
  {
    icon: UserCheck,
    tag: 'Security',
    title: 'Visitor Management System',
    desc: 'AI face-match, QR passes, host notifications and audit trails across every gate and reception.',
    outcomes: ['Faster check-ins', 'Full audit compliance', 'Multi-site rollout'],
  },
  {
    icon: LayoutDashboard,
    tag: 'Executive',
    title: 'Management Information System',
    desc: 'Unified MIS with live dashboards, semantic data layer and AI-generated executive summaries.',
    outcomes: ['Real-time KPIs', 'Cross-BU visibility', 'AI narratives'],
  },
  {
    icon: MessageSquareWarning,
    tag: 'CX',
    title: 'Complaint Management System',
    desc: 'Omnichannel intake, AI triage, SLA tracking, escalation paths and CSAT scoring.',
    outcomes: ['−58% resolution time', 'SLA visibility', 'AI triage'],
  },
  {
    icon: FileCheck2,
    tag: 'Safety',
    title: 'Work Permit Management',
    desc: 'Digital permit-to-work with role-based approvals, LOTO, and AI hazard detection.',
    outcomes: ['ISO 45001 ready', '−72% incidents', 'Full audit trail'],
  },
  {
    icon: Building2,
    tag: 'Operations',
    title: 'Facility Management ERP',
    desc: 'Assets, PPM, work orders, inventory, vendors and finance in a single, mobile-first platform.',
    outcomes: ['−31% OpEx', '+2.4x asset uptime', 'Mobile field ops'],
  },
  {
    icon: Boxes,
    tag: 'Supply Chain',
    title: 'Inventory Management',
    desc: 'Real-time stock, multi-warehouse, AI-driven reorder and low-touch procurement workflows.',
    outcomes: ['Zero stockouts', 'AI reorder', 'Multi-warehouse'],
  },
  {
    icon: Users,
    tag: 'People',
    title: 'HRMS',
    desc: 'Modern HR platform &mdash; recruit, onboard, pay, review and develop &mdash; with AI-assisted flows.',
    outcomes: ['End-to-end HR', 'AI assist', 'Compliance-ready'],
  },
  {
    icon: Bot,
    tag: 'AI',
    title: 'AI Automation Platform',
    desc: 'Enterprise AI agents with guardrails, human-in-the-loop, observability and cost control.',
    outcomes: ['11,000+ hours saved / mo', '−46% error rate', 'Fast payback'],
  },
  {
    icon: BarChart3,
    tag: 'Analytics',
    title: 'Analytics Dashboard',
    desc: 'Product, ops and revenue dashboards that decision-makers actually open every morning.',
    outcomes: ['Executive-ready', 'Live data', 'Mobile access'],
  },
  {
    icon: PieChart,
    tag: 'BI',
    title: 'Business Intelligence',
    desc: 'Warehouse + semantic layer + self-serve BI so every team gets to answers without a ticket.',
    outcomes: ['Self-serve BI', 'Single source of truth', 'Governed access'],
  },
  {
    icon: Wrench,
    tag: 'Bespoke',
    title: 'Custom Dashboards &amp; Software',
    desc: 'Tailor-made internal tools, admin consoles and workflow apps &mdash; shipped in weeks, not quarters.',
    outcomes: ['Ship in weeks', 'Your workflow', 'Own the IP'],
  },
];

export default function Solutions() {
  return (
    <div data-testid="solutions-page" className="pb-24">
      <PageHeader
        eyebrow="Solutions"
        title={<>Pre-shaped <span className="font-serif">enterprise solutions,</span> tailored to your business.</>}
        subtitle="Battle-tested platforms and playbooks &mdash; delivered by a senior team, extended and owned by yours."
      >
        <div className="flex flex-wrap gap-3">
          <Button to="/contact" variant="primary">Talk to an architect</Button>
          <Button to="/services" variant="secondary">Browse services</Button>
        </div>
      </PageHeader>

      <Section>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {solutions.map(({ icon: Icon, tag, title, desc, outcomes }, i) => (
            <div
              key={i}
              data-testid={`solution-card-${i}`}
              className="rounded-[32px] bg-white border border-[#7C3AED]/12 p-8 shadow-[0_4px_16px_rgba(0,0,0,0.04)] hover:shadow-[0_10px_40px_rgba(109,40,217,0.18)] hover:border-[#7C3AED]/35 hover:-translate-y-1 transition-all duration-300 flex flex-col"
            >
              <div className="flex items-center gap-2 mb-6">
                <div className="w-10 h-10 rounded-2xl grad-icon text-white flex items-center justify-center shadow-[0_6px_20px_rgba(109,40,217,0.28)]">
                  <Icon className="w-4 h-4" />
                </div>
                <span className="text-xs font-mono uppercase tracking-widest text-[#273C46]">{tag}</span>
              </div>
              <h3
                className="font-serif text-2xl text-[#0D212C] mb-3"
                dangerouslySetInnerHTML={{ __html: title }}
              />
              <p
                className="text-sm text-[#273C46] leading-relaxed mb-6"
                dangerouslySetInnerHTML={{ __html: desc }}
              />
              <ul className="mt-auto flex flex-col gap-2">
                {outcomes.map((o) => (
                  <li key={o} className="text-sm text-[#0D212C] flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#051A24] mt-2" />
                    {o}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
